// The item wizard's "Flexibility" section: what the restaurant is willing to
// change. Four string dimensions (brand / origin / state / spec) in a 2×2 grid,
// then packaging full-width beneath them — its value line and format chips are
// far wider than a brand name, and a fifth grid cell would leave a ragged row.
//
// Every dimension renders through one FlexibilityCard, so the "open to switch"
// chrome and the "Accepts only:" hint exist once. Candidates are catalog-driven:
// only values that exist on ACTIVE variants of the family are offered.

import type { MasterProduct } from 'apis/master-products';
import { Box, Checkbox, Flex, FormControl, FormLabel, Grid, Input, Text, Wrap, WrapItem } from 'design-system';
import {
  distinctPackagingSpecs,
  packagingSpecKey,
  packagingSpecLabel,
  packagingSpecOf,
} from 'pages/inquiry-processor/utils/packaging-spec';
import { attrOptions } from 'pages/inquiry-processor/utils/variant-elimination';
import { draftPackagingSpec, WizardDraft } from './wizard-draft';

export interface FlexibilityCandidate {
  key: string;
  label: string;
}

interface FlexibilityCardProps {
  label: string;
  valueText: string;
  isOpen: boolean;
  isDisabled?: boolean;
  onToggle: (open: boolean) => void;
  candidates: FlexibilityCandidate[];
  selectedKeys: string[];
  // Independent of `candidates`: a stored alternative that's dropped out of
  // the live catalog (e.g. its only variant went inactive) still gets saved
  // by buildAlternatives, so the hint must keep naming it even though it has
  // no checkbox to show for it — otherwise the hint claims "open to any"
  // while the PATCH still narrows to that stale value.
  selectedLabels: string[];
  onToggleCandidate: (key: string, checked: boolean) => void;
}

export const FlexibilityCard = ({
  label,
  valueText,
  isOpen,
  isDisabled,
  onToggle,
  candidates,
  selectedKeys,
  selectedLabels,
  onToggleCandidate,
}: FlexibilityCardProps) => {
  return (
    <Box borderWidth='1px' rounded='md' px='3' py='2'>
      <Flex justify='space-between' align='center'>
        <Box minW='0'>
          <Text fontSize='xs' color='gray.500'>
            {label}
          </Text>
          <Text fontSize='sm'>{valueText || 'N/A'}</Text>
        </Box>
        <Checkbox isChecked={isOpen} isDisabled={isDisabled} onChange={(e) => onToggle(e.target.checked)}>
          <Box as='span' fontSize='xs' whiteSpace='nowrap'>
            Open to switch
          </Box>
        </Checkbox>
      </Flex>
      {isOpen && candidates.length > 0 && (
        <Box mt='2' pt='2' borderTopWidth='1px'>
          <Wrap spacing='3'>
            {candidates.map((candidate) => (
              <WrapItem key={candidate.key}>
                <Checkbox
                  size='sm'
                  isChecked={selectedKeys.includes(candidate.key)}
                  isDisabled={isDisabled}
                  onChange={(e) => onToggleCandidate(candidate.key, e.target.checked)}>
                  <Box as='span' fontSize='xs'>
                    {candidate.label}
                  </Box>
                </Checkbox>
              </WrapItem>
            ))}
          </Wrap>
          <Text fontSize='xs' color='gray.500' mt='1'>
            {selectedLabels.length ? `Accepts only: ${selectedLabels.join(', ')}` : 'None selected — open to any'}
          </Text>
        </Box>
      )}
    </Box>
  );
};

type AltAttr = 'brand' | 'origin' | 'state' | 'spec';
type AltFlag = 'brandOS' | 'originOS' | 'stateOS' | 'specOS';

const STRING_DIMENSIONS: Array<{ label: string; attr: AltAttr; flag: AltFlag }> = [
  { label: 'Brand', attr: 'brand', flag: 'brandOS' },
  { label: 'Origin', attr: 'origin', flag: 'originOS' },
  { label: 'State', attr: 'state', flag: 'stateOS' },
  { label: 'Spec', attr: 'spec', flag: 'specOS' },
];

interface FlexibilityFieldsProps {
  draft: WizardDraft;
  // Active variants of the draft's family — the source of every candidate.
  activeVariants: MasterProduct[];
  locked: boolean;
  onPatch: (patch: Partial<WizardDraft>) => void;
}

export const FlexibilityFields = ({ draft, activeVariants, locked, onPatch }: FlexibilityFieldsProps) => {
  const toggleStringAlt = (attr: AltAttr, value: string, checked: boolean) => {
    const current = draft.alt[attr] ?? [];
    const list = checked ? [...current, value] : current.filter((v) => v !== value);
    const alt = { ...draft.alt };
    if (list.length) alt[attr] = list;
    else delete alt[attr];
    onPatch({ alt });
  };

  const currentPackaging = draftPackagingSpec(draft);
  const currentPackagingKey = packagingSpecKey(currentPackaging);
  const baseUnit = draft.sel.baseUnit;

  // Packaging candidates must come only from variants sharing the draft's own
  // baseUnit — baseUnit is never itself flexible (see utils/packaging-spec),
  // and attributesCompatible rejects any candidate whose baseUnit differs
  // before the packaging block is ever consulted, so offering a pack format
  // from another unit would create an alternative that can never be
  // satisfied. A draft with no baseUnit yet (no variant resolved) filters
  // nothing out — an unknown request unit is a wildcard, same as an unknown
  // field on either side of packagingCompatible.
  const normalizedBaseUnit = baseUnit ? baseUnit.trim().toLowerCase() : null;
  const packagingVariants = normalizedBaseUnit
    ? activeVariants.filter((v) => (v.baseUnit ?? '').trim().toLowerCase() === normalizedBaseUnit)
    : activeVariants;

  // Same pruning as buildAlternatives: the variant's own format is not an
  // alternative, so it gets no checkbox and can never be saved.
  const packagingCandidates: FlexibilityCandidate[] = distinctPackagingSpecs(packagingVariants)
    .filter((spec) => packagingSpecKey(spec) !== currentPackagingKey)
    .map((spec) => ({ key: packagingSpecKey(spec), label: packagingSpecLabel(spec, baseUnit) }));

  const selectedPackagingKeys = (draft.alt.packaging ?? [])
    .map((spec) => packagingSpecKey(packagingSpecOf(spec)))
    .filter((key) => key !== currentPackagingKey);

  // Same filter as selectedPackagingKeys, but resolved to labels straight
  // from the stored specs — not from packagingCandidates — so a stale
  // alternative (no longer offered by any active variant) still names
  // itself in the hint. See the selectedLabels prop's comment.
  const selectedPackagingLabels = (draft.alt.packaging ?? [])
    .map(packagingSpecOf)
    .filter((spec) => packagingSpecKey(spec) !== currentPackagingKey)
    .map((spec) => packagingSpecLabel(spec, baseUnit));

  const togglePackagingAlt = (key: string, checked: boolean) => {
    // Same baseUnit-filtered list the card was offered, so a key can never
    // resolve to a spec the card never displayed as a candidate.
    const byKey = new Map(distinctPackagingSpecs(packagingVariants).map((spec) => [packagingSpecKey(spec), spec]));
    const current = (draft.alt.packaging ?? []).map(packagingSpecOf);
    const next = checked
      ? [...current, byKey.get(key)].filter((spec): spec is NonNullable<typeof spec> => !!spec)
      : current.filter((spec) => packagingSpecKey(spec) !== key);
    const alt = { ...draft.alt };
    if (next.length) alt.packaging = next;
    else delete alt.packaging;
    onPatch({ alt });
  };

  return (
    <>
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap='2' mb='2'>
        {STRING_DIMENSIONS.map(({ label, attr, flag }) => {
          const value = draft.sel[attr] ?? '';
          // A stale pick equal to the current variant's value has no checkbox
          // and won't be saved — keep the hint honest about what save() stores.
          const candidates = attrOptions(activeVariants, attr)
            .filter((v) => v && v !== value)
            .map((v) => ({ key: v, label: v }));
          const selectedKeys = (draft.alt[attr] ?? []).filter((v) => v && v !== value);
          return (
            <FlexibilityCard
              key={attr}
              label={label}
              valueText={value}
              isOpen={draft[flag]}
              isDisabled={locked}
              onToggle={(open) => onPatch({ [flag]: open } as Partial<WizardDraft>)}
              candidates={candidates}
              selectedKeys={selectedKeys}
              // Strings are their own labels, and selectedKeys already applies
              // the same "not empty, not the current value" filter the old
              // flag() used for its hint — no catalog intersection needed.
              selectedLabels={selectedKeys}
              onToggleCandidate={(key, checked) => toggleStringAlt(attr, key, checked)}
            />
          );
        })}
      </Grid>

      <Box mb='3'>
        <FlexibilityCard
          label='Packaging'
          valueText={packagingSpecLabel(currentPackaging, baseUnit)}
          isOpen={draft.packagingOS}
          isDisabled={locked}
          onToggle={(open) => onPatch({ packagingOS: open })}
          candidates={packagingCandidates}
          selectedKeys={selectedPackagingKeys}
          selectedLabels={selectedPackagingLabels}
          onToggleCandidate={togglePackagingAlt}
        />
      </Box>

      <FormControl>
        <FormLabel fontSize='sm'>Notes</FormLabel>
        <Input
          size='sm'
          placeholder='Per-item note'
          value={draft.notes}
          isDisabled={locked}
          onChange={(e) => onPatch({ notes: e.target.value })}
        />
      </FormControl>
    </>
  );
};
